import { useContext, useEffect } from 'react';
import { keepPreviousData, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { supportsMarketData } from '../utils/brokerConnection';
import { resolvePerformanceRangeParams } from '../utils/dateRanges';

const getBackendUrl = () => process.env.REACT_APP_BACKEND_URL || '';

async function fetchPerformanceEndpoint(endpoint, params, token) {
  const url = new URL(`${getBackendUrl()}/performance/${endpoint}`);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    });
  }

  const res = await fetch(url.toString(), {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!res.ok) {
    const err = new Error(`Performance API ${endpoint} failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/**
 * Fetches all performance data for the PerformanceDashboard.
 *
 * Accepts preset `timeframe` or custom `dateRange: { since, until }` (YYYY-MM-DD).
 * Returns live API data when available; gracefully falls back to empty/zero
 * state so the UI degrades cleanly while the trade lifecycle pipeline populates.
 *
 * Benchmark (SPY/QQQ) is only requested from `/performance/equity` when an
 * effective MD source is available.
 */
export function usePerformanceData({
  timeframe = 'month',
  dateRange = null,
  calendarDateRange = null,
  accountId,
  account = null,
  paperTrading,
  benchmark = 'SPY',
  includeOpenPositions = false,
  enabled: externalEnabled,
} = {}) {
  const { user } = useContext(AuthContext);
  const token = user?.token;
  const queryClient = useQueryClient();
  const location = useLocation();
  // Allow callers to gate queries (e.g. until selectedAccount is known).
  // Default to enabled whenever we have a token.
  const enabled = !!token && (externalEnabled !== false);
  const normalizedAccountId =
    accountId !== undefined && accountId !== null ? String(accountId) : undefined;
  const canRequestBenchmark = supportsMarketData(user, account);
  const effectiveBenchmark = canRequestBenchmark ? benchmark || 'SPY' : null;

  const perfQueryOpts = {
    enabled,
    staleTime: 30000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    // Keep prior account/timeframe data visible while the next key loads (avoids empty chart flash).
    placeholderData: keepPreviousData,
  };

  // Refetch when navigating to performance (e.g. trade → performance) so closed trades appear.
  useEffect(() => {
    if (!enabled) return;
    if (location.pathname === '/performance' || location.pathname.includes('health')) {
      queryClient.invalidateQueries({ queryKey: ['performance'] });
    }
  }, [location.pathname, enabled, queryClient]);

  const isCustomRange = !!(dateRange?.since && dateRange?.until);
  const rangeParams = resolvePerformanceRangeParams(timeframe, dateRange);
  const params = { ...rangeParams };
  if (normalizedAccountId) params.accountId = normalizedAccountId;
  if (paperTrading !== undefined) params.paperTrading = paperTrading;
  if (effectiveBenchmark) params.benchmark = effectiveBenchmark;
  if (includeOpenPositions) params.includeOpenPositions = 'true';

  const rangeKeyKind = rangeParams.since ? (isCustomRange ? 'custom' : 'ytd') : timeframe;
  const baseQueryKey = [
    'performance',
    rangeKeyKind,
    rangeParams.since || null,
    rangeParams.until || null,
    normalizedAccountId,
    paperTrading,
    effectiveBenchmark,
    includeOpenPositions,
  ];

  const summaryQuery = useQuery({
    queryKey: [...baseQueryKey, 'summary'],
    queryFn: () => fetchPerformanceEndpoint('summary', params, token),
    ...perfQueryOpts,
    retry: 1,
  });

  const equityQuery = useQuery({
    queryKey: [...baseQueryKey, 'equity'],
    queryFn: () => fetchPerformanceEndpoint('equity', params, token),
    ...perfQueryOpts,
    retry: 1,
  });

  // Calendar is always closed-trades only (never includeOpenPositions).
  const calendarParams =
    calendarDateRange?.since && calendarDateRange?.until
      ? {
          ...(normalizedAccountId ? { accountId: normalizedAccountId } : {}),
          ...(paperTrading !== undefined ? { paperTrading } : {}),
          ...(effectiveBenchmark ? { benchmark: effectiveBenchmark } : {}),
          since: calendarDateRange.since,
          until: calendarDateRange.until,
        }
      : {
          ...rangeParams,
          ...(normalizedAccountId ? { accountId: normalizedAccountId } : {}),
          ...(paperTrading !== undefined ? { paperTrading } : {}),
          ...(effectiveBenchmark ? { benchmark: effectiveBenchmark } : {}),
        };
  const calendarQuery = useQuery({
    queryKey: [
      'performance',
      rangeKeyKind,
      rangeParams.since || null,
      rangeParams.until || null,
      normalizedAccountId,
      paperTrading,
      'calendar',
      calendarDateRange?.since || null,
      calendarDateRange?.until || null,
    ],
    queryFn: () => fetchPerformanceEndpoint('calendar', calendarParams, token),
    ...perfQueryOpts,
    retry: 1,
  });

  const distributionQuery = useQuery({
    queryKey: [...baseQueryKey, 'distribution'],
    queryFn: () => fetchPerformanceEndpoint('distribution', params, token),
    ...perfQueryOpts,
    retry: 1,
  });

  const diagnosisQuery = useQuery({
    queryKey: [...baseQueryKey, 'diagnosis'],
    queryFn: () => fetchPerformanceEndpoint('diagnosis', params, token),
    enabled,
    staleTime: 300000, // diagnosis is slow to change
    retry: 1,
  });

  const isLoading = summaryQuery.isLoading || equityQuery.isLoading;
  const isEquityLoading = equityQuery.isFetching;
  const isError = summaryQuery.isError || equityQuery.isError;

  // Empty/zero fallbacks so UI renders without crashing when data is not yet available
  const emptySummary = {
    netPnl: 0,
    winRate: 0,
    profitFactor: 0,
    expectancy: 0,
    avgWin: 0,
    avgLoss: 0,
    totalTrades: 0,
    winningTrades: 0,
    losingTrades: 0,
    maxDrawdown: null,
    bestTrade: 0,
    worstTrade: 0,
    currentEquity: null,
    allTimeHigh: null,
    todayPnl: null,
    tradePnls: [],
  };

  return {
    summary: summaryQuery.data || emptySummary,
    equity: equityQuery.data || {
      equityCurve: [],
      drawdownCurve: [],
      rangeStart: null,
      rangeEnd: null,
      accountPnlPct: null,
      benchmarkPnlPct: null,
      benchmark: effectiveBenchmark || benchmark || 'SPY',
      benchmarkAvailable: false,
    },
    calendar: calendarQuery.data || { days: [] },
    distribution: distributionQuery.data || { bins: [] },
    diagnosis: diagnosisQuery.data || { strengths: [], weaknesses: [], observations: [] },
    isLoading,
    isEquityLoading,
    isError,
  };
}

/**
 * Closed trades for a single calendar day (ET). Fetches when modal is open.
 */
const RECENT_TRADES_PAGE_SIZE = 25;

/** Allowed `sortBy` values for GET /performance/recent-trades (sort before pagination). */
const RECENT_TRADES_SORT_FIELDS = [
  'openDate',
  'closeDate',
  'symbol',
  'pnl',
  'journaled',
  'notJournaled',
];

/**
 * Infinite-scroll trades list — not bound to the performance page timeframe.
 *
 * Sort is applied server-side across the full set, then paginated.
 * `sortBy` / `sortDir` are forwarded as query params; backend must ORDER BY before LIMIT/OFFSET.
 * Open rows should still sort ahead of closed when includeOpenPositions is true.
 *
 * Returns a useInfiniteQuery result. Flatten pages with:
 * `data?.pages?.flatMap((p) => p.positions ?? []) ?? []`
 */
export function usePerformanceRecentTrades({
  accountId,
  paperTrading,
  symbol = '',
  limit = RECENT_TRADES_PAGE_SIZE,
  includeOpenPositions = true,
  sortBy = 'closeDate',
  sortDir = 'desc',
  enabled: externalEnabled,
} = {}) {
  const { user } = useContext(AuthContext);
  const token = user?.token;
  const enabled = !!token && !!accountId && (externalEnabled !== false);
  const symbolQuery = (symbol || '').trim();
  const normalizedSortBy = RECENT_TRADES_SORT_FIELDS.includes(sortBy) ? sortBy : 'closeDate';
  const normalizedSortDir = sortDir === 'asc' ? 'asc' : 'desc';

  return useInfiniteQuery({
    queryKey: [
      'performance',
      'recent-trades',
      accountId,
      paperTrading,
      limit,
      symbolQuery,
      includeOpenPositions,
      normalizedSortBy,
      normalizedSortDir,
    ],
    queryFn: ({ pageParam = 1 }) =>
      fetchPerformanceEndpoint(
        'recent-trades',
        {
          accountId,
          paperTrading,
          page: pageParam,
          limit,
          sortBy: normalizedSortBy,
          sortDir: normalizedSortDir,
          ...(symbolQuery ? { symbol: symbolQuery } : {}),
          ...(includeOpenPositions ? { includeOpenPositions: 'true' } : { includeOpenPositions: 'false' }),
        },
        token
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage?.hasMore) return allPages.length + 1;
      return undefined;
    },
    enabled,
    staleTime: 30000,
    refetchOnMount: 'always',
    placeholderData: keepPreviousData,
    retry: 1,
  });
}

export { RECENT_TRADES_PAGE_SIZE, RECENT_TRADES_SORT_FIELDS };

export function usePerformanceDayDetail({
  dateKey,
  accountId,
  paperTrading,
  enabled = false,
} = {}) {
  const { user } = useContext(AuthContext);
  const token = user?.token;

  return useQuery({
    queryKey: ['performance', 'calendar-day', dateKey, accountId, paperTrading],
    queryFn: () =>
      fetchPerformanceEndpoint(
        'calendar/day',
        { date: dateKey, accountId, paperTrading },
        token
      ),
    enabled: !!token && !!dateKey && enabled,
    staleTime: 60000,
    retry: 1,
  });
}
